/**
 * The owner and target boundary of every exported supplier invoice screen (batch 8 T12c3, #3033).
 *
 * Drafts, a displayed review, a selected document, names, an open dialog and a request's state belong to the signed-in owner
 * (tenant, user, session family, generation), to the rights that owner holds and to the purchase order or invoice on screen. The key
 * is read in the very render that sees any of them change, so no frame shows another owner's figures, a downgraded session's
 * figures or another order's figures, and nothing of the old subtree survives to report a late result. Unresolved writes are not
 * state of the subtree: they live in the owner's journal and are found again by their own owner.
 *
 * Every exported component (`SupplierInvoicesPanel`, `SupplierInvoiceDialog`, `SupplierInvoiceDetailDialog`) wraps itself in this,
 * so an integrating page cannot forget it.
 */

import { Fragment, useEffect, useRef, type ReactNode } from 'react';
import { useInvoiceCacheGuard } from '@/hooks/useSupplierInvoices';
import { accessKey, useInvoiceAccess } from './invoiceSession';

export function InvoiceBoundary({ target, children }: { target: string; children: ReactNode }) {
  const access = useInvoiceAccess();
  useInvoiceCacheGuard();
  return <Fragment key={`${accessKey(access)}|${target}`}>{children}</Fragment>;
}

/** A function that says whether the screen that created it is still mounted: the `targetCurrent` of its fences. */
export function useStillShown(): () => boolean {
  const shown = useRef(true);
  useEffect(() => {
    shown.current = true;
    return () => {
      shown.current = false;
    };
  }, []);
  return () => shown.current;
}
